import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Save, Send, Database, FileText, Check, AlertCircle, ShoppingBag, ShieldAlert, Cpu, Share2 } from 'lucide-react';
import { NexoraXSortDropdown } from './NexoraXSortDropdown';
import { SearchResult, NexusUser } from '../types/nexus';

interface NexusSearchProps {
  user: NexusUser;
  onSearchExecuted: (cost: number, query: string) => void;
  onSendToAgent: (data: string) => void;
  onSendToWorkSuite: (data: string) => void;
  isAdmin: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const NexusSearch: React.FC<NexusSearchProps> = ({
  user,
  onSearchExecuted,
  onSendToAgent,
  onSendToWorkSuite,
  isAdmin,
  onAddLog
}) => {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('Web Search');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [aiSummary, setAiSummary] = useState('');
  const [searchProvider, setSearchProvider] = useState<'Google' | 'Brave' | 'Azure' | 'Internal' | 'Hybrid'>('Google');
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'priority' | 'name' | 'lastModified'>('priority');

  const sortedResults = [...results].sort((a, b) => {
    if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'lastModified') {
      const aTime = a.date ? (a.date.includes('June') ? 3 : a.date.includes('May') ? 2 : 1) : 0;
      const bTime = b.date ? (b.date.includes('June') ? 3 : b.date.includes('May') ? 2 : 1) : 0;
      return bTime - aTime;
    } else if (sortBy === 'priority') {
      const sourcePriority = (type?: string) => {
        if (!type) return 1;
        if (type.toLowerCase().includes('whitepaper') || type.toLowerCase().includes('advisory')) return 3;
        if (type.toLowerCase().includes('platform') || type.toLowerCase().includes('sku')) return 2;
        return 1;
      };
      const aPri = sourcePriority(a.sourceType);
      const bPri = sourcePriority(b.sourceType);
      if (aPri !== bPri) {
        return bPri - aPri;
      }
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  const searchTypes = [
    'Web Search', 'Business Search', 'Product Search', 'Local Business Search',
    'Competitor Search', 'Supplier Search', 'News Search', 'Website Search',
    'Client Knowledge Search', 'Agent Search'
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    if (user.searchCredits <= 0) {
      onAddLog('Nexus Search', `Query: "${query}"`, 'DENIED', 'Zero search credits remaining. Access blocked.');
      alert('Insufficient search credits! Please top up your Credits or upgrade your plan.');
      return;
    }

    setIsSearching(true);
    setSearchCompleted(false);

    setTimeout(() => {
      let mockResults: SearchResult[] = [];
      let summary = '';

      if (searchType === 'Competitor Search' || query.toLowerCase().includes('competitor') || query.toLowerCase().includes('vs')) {
        mockResults = [
          { id: '1', title: 'Aetheria Systems Global Competitive Matrix', url: 'https://aetheriasystems.com/competitors', snippet: 'Comprehensive pricing structures, SLA guidelines, and deployment cycle lengths for core web automation tiers.', sourceType: 'Competitor Platform', date: 'June 2026' },
          { id: '2', title: 'Market Intell: Enterprise SaaS Providers Market Share', url: 'https://marketintell.net/saas-dashboard', snippet: 'Tracking the rise of NexoraX sandboxed setups vs standard multi-tenant hosting schemas. Leading nodes mapped.', sourceType: 'Analytics Ledger', date: 'April 2026' },
          { id: '3', title: 'Apex Cloud Logistics Core Deployment Tariffs', url: 'https://apexcloud.logistics/pricing', snippet: 'Public catalog detailing high-volume API endpoint metering, monthly container baseline fees, and multi-region failovers.', sourceType: 'Competitor Listing' }
        ];
        summary = `Competitor Analysis complete for query: "${query}". Major players (Aetheria Systems, Apex Cloud) operate on multi-tenant SaaS structures with higher latencies (avg 180ms) compared to Nexus OS bare-metal nodes (<15ms). Aetheria maintains a larger market share but is bound to high-friction contract locking, leaving a substantial gap in high-integrity automated operations. Recommended Action: Initialize outbound campaign emphasizing security and speed advantages using UGC Ad scripts.`;
      } else if (searchType === 'Product Search' || query.toLowerCase().includes('price') || query.toLowerCase().includes('buy')) {
        mockResults = [
          { id: '1', title: 'Industrial Grade IoT Edge Gateways Class 4', url: 'https://directsensors.co.uk/iot-class-4', snippet: 'Secure telemetry router with integrated dual-mode failover protocols, local SD storage buffering, and custom environment variable shielding.', sourceType: 'Supplier SKU', date: 'May 2026' },
          { id: '2', title: 'Hetzner Dedicated Server PX Series PX93', url: 'https://hetzner-baremetal.de/px93', snippet: 'AMD EPYC processors, 128GB RAM configuration, redundant power slots, and dedicated Nginx reverse proxy routing controllers.', sourceType: 'Hosting Catalog', date: 'Recent' }
        ];
        summary = `Product analysis for "${query}": Found matching items. Recommended supplier Hetzner Dedicated PX Series provides optimal bare-metal groundings for low-latency nodes. Dual-sensor Class 4 edge gateways are suitable for IoT telemetry streaming into Nexus Workspace Chronos Sheets. Unified direct-billing packages can offset upfront procurement.`;
      } else {
        mockResults = [
          { id: '1', title: 'NexoraX Digital Transformation Integration Metrics', url: 'https://nexorax.co.uk/docs/whitepaper', snippet: 'How sandboxed workspace systems and dedicated node networks achieve an elite 0.8s execution standard with robust localized compliance checks.', sourceType: 'Whitepaper', date: 'June 2026' },
          { id: '2', title: 'UK NexoraX billing records Advisory Guideline (NexoraX)', url: 'https://gov.uk/access-to-work/allocations', snippet: 'Official protocol definitions for matching organizational stipends, direct-billing setups, and NexoraX account verifications.', sourceType: 'Public Advisory', date: 'March 2026' },
          { id: '3', title: 'NexoraX Cloud Deployments & Core Container Orchestration', url: 'https://NexoraXcloud.org/handbook', snippet: 'Best practices for organizing multi-tenant cloud storage structures and setting up secure metadata-guided Nginx load balancers.', sourceType: 'Technical Manual' }
        ];
        summary = `Successfully retrieved data on "${query}". The search mapped multiple verified cloud schemas. Interconnected nodes can compile findings and trigger ad briefs directly into Nexus Media Studio. Total cost calculated under server-side API rate protocols. No malicious redirections detected.`;
      }

      setResults(mockResults);
      setAiSummary(summary);
      setIsSearching(false);
      setSearchCompleted(true);
      onSearchExecuted(1, query); // 1 credit = 1p
      onAddLog('Nexus Search', `Search Query: "${query}" (${searchType})`, 'SUCCESS', `Processed successfully via ${searchProvider} API. 1 Credit deducted.`);
    }, 1500);
  };

  return (
    <div className="w-full space-y-6">
      {/* Search Header Banner */}
      <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/10 via-black/40 to-cyan-950/10 shadow-[0_0_25px_rgba(0,255,255,0.05)]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
              <Search className="w-6 h-6 text-[#00ffff] animate-pulse" />
              Nexus Search
            </h2>
            <p className="text-xs font-mono text-cyan-400/80 uppercase tracking-widest mt-1">
              Search. Host. Automate. Create. Scale.
            </p>
            <p className="text-xs text-slate-400 mt-2 max-w-2xl font-sans normal-case leading-relaxed">
              AI-Powered Search for Business Discovery. Search the web, research competitors, find key products, and pipeline raw findings straight into automated agents and the WorkSuite.
            </p>
          </div>
          <div className="bg-black/60 border border-cyan-500/20 p-4 rounded-xl text-right font-mono min-w-[200px]">
            <div className="text-[10px] text-slate-500 uppercase">Search Tariffs</div>
            <div className="text-xl font-bold text-white tracking-tight pt-1">
              £0.01 <span className="text-xs text-[#00ffff]">/ Search</span>
            </div>
            <div className="text-[9px] text-cyan-400/70 uppercase mt-1">
              Credits Remaining: <strong className="text-white">{user.searchCredits}</strong> ({user.searchCredits} searches)
            </div>
          </div>
        </div>

        {/* Search Mode Choices */}
        <div className="mt-6 border-t border-cyan-950/40 pt-4">
          <div className="flex flex-wrap gap-2">
            {searchTypes.map((t) => (
              <button
                key={t}
                onClick={() => setSearchType(t)}
                className={`py-1.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                  searchType === t
                    ? 'bg-[#00ffff]/15 text-[#00ffff] border-[#00ffff]/30 shadow-[0_0_10px_rgba(0,255,255,0.15)]'
                    : 'bg-transparent text-slate-500 border-zinc-800 hover:text-slate-300'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Search Bar */}
      <div className="glass-panel border border-purple-500/15 p-5 rounded-2xl bg-black/40">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={`Query "${searchType}"...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-black/50 border border-zinc-850 rounded-xl py-3 pl-11 pr-4 text-xs font-mono text-white tracking-wider outline-none focus:border-[#00ffff]/50 transition-colors uppercase"
            />
          </div>
          <button
            type="submit"
            disabled={isSearching}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-[#00ffff] text-black font-mono text-xs font-black uppercase rounded-xl hover:opacity-90 active:scale-97 transition-all flex items-center gap-2 cursor-pointer shadow-[0_0_15px_rgba(0,255,255,0.25)] min-w-[140px] justify-center"
          >
            {isSearching ? 'SEARCHING...' : 'EXECUTE'}
          </button>
        </form>

        {/* Options Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 text-[9px] font-mono text-slate-500 border-t border-zinc-900 pt-3">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 font-extrabold">PROVIDER MODE:</span>
            {['Google', 'Brave', 'Azure', 'Internal', 'Hybrid'].map((prov) => (
              <label key={prov} className="flex items-center gap-1.5 cursor-pointer hover:text-white">
                <input
                  type="radio"
                  name="provider_mode"
                  checked={searchProvider === prov}
                  onChange={() => setSearchProvider(prov as any)}
                  className="accent-[#00ffff]"
                />
                {prov}
              </label>
            ))}
          </div>
          <div>
            <span>* Google API & Azure Grounding configured server-side (Keys fully secure)</span>
          </div>
        </div>
      </div>

      {/* Loader */}
      {isSearching && (
        <div className="flex flex-col items-center justify-center py-20 bg-black/30 border border-cyan-500/10 rounded-2xl">
          <Cpu className="w-8 h-8 text-[#00ffff] animate-spin mb-4" />
          <p className="text-xs font-mono tracking-widest text-[#00ffff] uppercase animate-pulse">
            Executing Multi-Channel Meta-Query...
          </p>
          <span className="text-[10px] text-slate-600 font-mono mt-2 uppercase">
            NexoraX API Rate Meter Active
          </span>
        </div>
      )}

      {/* Search Results Display */}
      {searchCompleted && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Results (2/3 col) */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Summary Block */}
            <div className="glass-panel border-l-4 border-l-[#00ffff] border border-purple-500/20 p-5 rounded-r-2xl bg-gradient-to-r from-[#00ffff]/5 via-black/40 to-black/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#00ffff] animate-ping" />
                <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                  Nexus Intelligence AI Summary
                </h3>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed font-sans font-medium">
                {aiSummary || 'Analysis generated on safe search parameters.'}
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    onSendToAgent(aiSummary);
                    onAddLog('Nexus Search', 'Send Summary to Agent', 'SUCCESS', 'Query summary sent to Node Agent queue.');
                    alert('Summary payload queued for Agent execution!');
                  }}
                  className="py-1.5 px-3 bg-cyan-950/40 hover:bg-cyan-500/10 text-[#00ffff] border border-cyan-500/20 rounded-lg text-[9px] font-mono uppercase font-black tracking-wider transition-colors cursor-pointer"
                >
                  Send to Agent
                </button>
                <button
                  onClick={() => {
                    onSendToWorkSuite(aiSummary);
                    onAddLog('Nexus Search', 'Send Summary to WorkSuite', 'SUCCESS', 'Summary details created as project document.');
                    alert('Workspace document generated from search details!');
                  }}
                  className="py-1.5 px-3 bg-purple-950/40 hover:bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded-lg text-[9px] font-mono uppercase font-black tracking-wider transition-colors cursor-pointer"
                >
                  Create Document
                </button>
              </div>
            </div>

            {/* Source Listings */}
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-2 pl-1 flex-wrap">
                <h3 className="text-xs font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
                  Verified Indexes Matches ({results.length})
                </h3>
                <NexoraXSortDropdown
                  options={[
                    { value: 'priority', label: 'Node Priority' },
                    { value: 'name', label: 'Name' },
                    { value: 'lastModified', label: 'Last Modified' }
                  ]}
                  selectedValue={sortBy}
                  onSelect={(val) => setSortBy(val as 'priority' | 'name' | 'lastModified')}
                />
              </div>
              {sortedResults.map((res, index) => (
                <div
                  key={res.id}
                  className="glass-panel border border-zinc-850 hover:border-cyan-500/10 p-5 rounded-2xl bg-black/30 hover:bg-black/60 transition-all group duration-300 relative"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-zinc-900 pb-2 mb-3">
                    <span className="text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-black">
                      {res.sourceType}
                    </span>
                    {res.date && (
                      <span className="text-[8px] font-mono text-slate-500 font-extrabold uppercase">
                        {res.date}
                      </span>
                    )}
                  </div>

                  <h4 className="text-xs font-sans font-extrabold text-white group-hover:text-[#00ffff] transition-colors uppercase tracking-tight">
                    {res.title}
                  </h4>
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[9.5px] font-mono text-slate-500 truncate block hover:underline w-full max-w-md pt-1 lowercase"
                  >
                    {res.url}
                  </a>
                  <p className="text-xs text-slate-400 leading-normal pt-2 font-medium">
                    {res.snippet}
                  </p>

                  {/* Actions Bar per result */}
                  <div className="mt-4 pt-3 border-t border-zinc-900/60 flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setSelectedActionIndex(index);
                        onAddLog('Nexus Search', `Save source: "${res.title}"`, 'SUCCESS', 'Source item bookmarked securely.');
                        setTimeout(() => setSelectedActionIndex(null), 1000);
                      }}
                      className="py-1 px-2.5 bg-black/60 hover:bg-zinc-900 text-slate-400 hover:text-white border border-zinc-800 rounded text-[8px] font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer"
                    >
                      {selectedActionIndex === index ? <Check size={10} className="text-emerald-400" /> : <Save size={10} />}
                      {selectedActionIndex === index ? 'Saved' : 'Save Source'}
                    </button>
                    <button
                      onClick={() => {
                        onSendToAgent(`Analyze this URL: ${res.url}. Details: ${res.snippet}`);
                        alert('Resource link dispatched to Nexus Agent!');
                      }}
                      className="py-1 px-2.5 bg-black/60 hover:bg-cyan-950/20 text-[#00ffff]/80 hover:text-[#00ffff] border border-cyan-500/10 rounded text-[8px] font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer"
                    >
                      <Send size={10} />
                      Send to Agent
                    </button>
                    <button
                      onClick={() => {
                        onSendToWorkSuite(`Add task from search result: ${res.title}. Link: ${res.url}`);
                        alert('Created WorkSuite action item from search metadata!');
                      }}
                      className="py-1 px-2.5 bg-black/60 hover:bg-purple-950/20 text-purple-300 hover:text-purple-200 border border-purple-500/10 rounded text-[8px] font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer"
                    >
                      <FileText size={10} />
                      Create Task
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Metrics Panel (1/3 col) */}
          <div className="space-y-6">
            <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 text-xs font-mono space-y-4">
              <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-[#00ffff]" />
                Search Overview
              </h3>
              <div className="space-y-2.5">
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">METERED TARIFF:</span>
                  <span className="text-white">£0.01 / successful query</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">COST OF THIS SEARCH:</span>
                  <span className="text-[#00ffff]">£0.01 (1 Credit)</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">RESOLUTION STATUS:</span>
                  <span className="text-emerald-400 flex items-center gap-1">
                    <Check size={11} /> INDEX MATCH
                  </span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">CACHED RESULT:</span>
                  <span className="text-slate-400">NO (REAL-TIME FETCH)</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">RATE_LIMITING_ABUSE:</span>
                  <span className="text-emerald-400">ACTIVE (0% THREAT)</span>
                </div>
              </div>
            </div>

            {/* Related Queries */}
            <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-3">
              <h4 className="text-[10px] font-mono font-black text-slate-400 uppercase tracking-widest">
                Suggested Agent Actions
              </h4>
              <div className="space-y-2">
                {[
                  'Research this company further',
                  'Build target supplier shortlist',
                  'Construct competitor ad pitch'
                ].map((act) => (
                  <button
                    key={act}
                    onClick={() => {
                      onSendToAgent(`Command action: ${act} regarding recent query ${query}`);
                      alert(`Initiated agent action: "${act}"`);
                    }}
                    className="w-full text-left py-2 px-3 bg-black/40 hover:bg-[#00ffff]/5 border border-zinc-850 hover:border-cyan-500/20 rounded-lg text-[10px] font-mono text-slate-400 hover:text-white transition-all capitalize block cursor-pointer"
                  >
                    🚀 {act}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
